import {
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useFormik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lodash from 'lodash';
import * as Yup from 'yup';
import CustomCheckbox from '../../../../components/ui/CustomCheckbox';
import FieldErrorText from '../../../../components/ui/FieldErrorText';
import {
  CarColorOptions,
  CarKeyOptions,
  DamageOptions,
  DamageOptionsConstants,
  DrivingCondition,
  mileageDrivingCondition,
  SellMyCarSteps,
  TransmissionOptions,
} from '../../../../utils/constants/sellMyCar';
import { mileageUnits, MyGarageSteps } from '../../../../utils/constants/myGarage';
import NavigationButtons from '../NavigationButtons';
import {
  damageContainer,
  formContainer,
  formControl,
  formStyle,
  getToggleButtonStyles,
  gridItem,
  inputLabel,
  labelText,
  select,
  subtitle,
  tiresAccessoriesContainer,
  toggleButtonGroup,
} from './GeneralInfoSx';
import CustomSelect from '../../../../components/ui/CustomSelect';
import theme from '../../../../theme';
import {
  setActiveStep,
  setGeneralInfoForm,
  setMyGarageMultipleFields,
  setVehicleId,
} from '../../../../toolkit/myGarage/myGarageSlice';
import {
  useLazySubmitCarFormQuery,
  useLazyUpdateCarQuery,
  useUpdateCarOnRelistPendingMutation,
} from '../../../../toolkit/myGarage/myGarageApi';
import { setAlertDialog } from '../../../../toolkit/alertDialog/alertDialogSlice';
import { emptyFieldsPartialForm } from './utils/emptyFieldsPartialForm';
import { errorContainer } from './QuestionareSx';
import { useTranslation } from 'react-i18next';
import { getCommaFormattedPrice } from '../../../../utils/methods/stringMethods';
import PriceInput from '../../../../components/custom/CustomInputs/PriceInput';
import CommaSeparatedInput from '../../../../components/custom/CustomInputs/CommaSeparatedInput';
import { CloseOutlined } from '@mui/icons-material';
import { isVehicleDataEditEnabled } from '../../../../utils/methods/generalVehicleMethods';
import { useGetVehicleDetailsQuery, useLazyGetVehicleDetailsQuery } from '../../../../toolkit/carDetails/carDetailsApi';
import { CAR_STATUS } from '../../../../utils/constants/carData';
import { ontarioCitiesArray } from '../../../../utils/constants/ontarioCitiesArray';

export const backendMappedValuesStep1 = {
  color: 'Color',
  key: 'Car_Keys',
  transmission: 'Automatic',
  exteriorDamage: {
    'Minor Damage': 'Minor_Damage',
    'Fading Paints': 'Fading_Paints',
    Rust: 'Rust',
    'Hail Damage': 'Hail_Damage',
    'Mint condition': 'Ext_Mint_Condition',
    Dents: 'Dents',
  },
  interiorDamage: {
    'Rips on Seats': 'Seats_Rips_Tears',
    'Visible Stains': 'Seats_Visible_Stain',
    'Strong Smell': 'Strong_Smell',
    'Mint condition': 'Int_Mint_Condition',
    'Damaged Systems': 'Nav_Entmt_Ctrl_Dmg_Sys',
    Others: 'Other_Interior_Damage',
  },
  tires: {
    '2 Sets of Tires': '2_Sets_Of_Tire',
    'Window Tint': 'Win_Tint',
    'Aftermarket Wheels': 'Aftermark_Exhaust',
    'Aftermarket Rims': 'Aftermark_Rims',
    'Roof Racks': 'Roof_Rack',
  },
  accessories: {
    'Remote Starter': 'Remote_Start',
    'Aftermarket Stereo': 'Aftermark_Stereo',
    'Aftermarket Spoiler': 'Aftermark_Spoiler',
  },
};

const validationSchema = Yup.object({
  Location: Yup.string().max(500).required('This field is required'),
  VIN: Yup.string().max(500).required('This field is required'),
  Year: Yup.string().trim().max(4).required('This field is required'),
  Make: Yup.string().max(500).required('This field is required'),
  Model: Yup.string().max(500).required('This field is required'),
  Trim: Yup.string().max(500).required('This field is required'),
  Mileage: Yup.number().min(500).required('This field is required'),
  color: Yup.string().max(500).required('This field is required'),
  key: Yup.string().max(500).required('This field is required'),
  transmission: Yup.string().max(500).required('This field is required'),
  exteriorDamage: Yup.array().min(1, 'Please select at least 1 item'),
  interiorDamage: Yup.array().min(1, 'Please select at least 1 item'),
  tires: Yup.array(),
  accessories: Yup.array(),
  check_driving_condition: Yup.string()
    .required('This field is required')
    .when('Mileage', (mileage, schema) => {
      if (mileage?.[0] > mileageDrivingCondition) {
        return schema
          .oneOf([DrivingCondition['Sold AS IS']], `Driving condition must be Sold AS IS`)
          .required('This field is required');
      } else {
        return schema.oneOf([DrivingCondition['Run and Drive'], DrivingCondition['Sold AS IS']]);
      }
    }),
});

export const GeneralInfo = ({ previousFormHandler }) => {
  const initialValues = {
    Location: '',
    VIN: '',
    Year: '',
    Make: '',
    Model: '',
    Trim: '',
    Mileage: '',
    Mileage_Unit: mileageUnits.km,
    color: '',
    key: '',
    transmission: TransmissionOptions.AUTOMATIC,
    exteriorDamage: [],
    interiorDamage: [],
    tires: [],
    accessories: [],
    check_driving_condition: DrivingCondition['Run and Drive'],
  };

  const decodeVIN = async (vin) => {
    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`);
      const data = await response.json();

      if (data.Results) {
        const results = data.Results;

        // Extract relevant fields from the API response
        const make = results.find((item) => item.Variable === 'Make')?.Value || '';
        const model = results.find((item) => item.Variable === 'Model')?.Value || '';
        const year = results.find((item) => item.Variable === 'Model Year')?.Value || '';
        const trim = results.find((item) => item.Variable === 'Trim')?.Value?.replace?.('/', '-') || '';
        const bodyClass = results.find((item) => item.Variable === 'Body Class')?.Value?.replace?.('/', '-') || '';
        const engineInfo = results.find((item) => item.Variable === 'Engine Model')?.Value || '';

        // Check if VIN is valid
        const errorCodes = results.filter((item) => item.ErrorCode && item.ErrorCode !== '0');
        if (errorCodes.length > 0) {
          // throw new Error("Invalid VIN number");
        }

        return {
          Make: make,
          Model: model,
          Year: year,
          Trim: trim || bodyClass, // Use trim or body class as fallback
          engineInfo,
          isValid: true,
        };
      } else {
        throw new Error('Invalid VIN number');
      }
    } catch (error) {
      throw new Error('Invalid VIN number or API error');
    }
  };

  const onMount = React.useRef({
    exteriorDamage: false,
    interiorDamage: false,
    setGeneralInfoForm: false,
    checkDrivingCondition: false,
    setTouchedExteriorDamage: false,
    setTouchedInteriorDamage: false,
  });

  const { t } = useTranslation('generalInfoForm');

  const dispatch = useDispatch();

  const isMobile = useMediaQuery('(max-width:1550px)');

  const { forms, previouslySubmittedForms, isEditing, vehicle_id, selectedVehicle } = useSelector(
    (store) => store.myGarage
  );
  const auth = useSelector((store) => store.auth);

  const [mileageUnit, setMileageUnit] = React.useState();

  const [submitCarForm, submitCarFormData] = useLazySubmitCarFormQuery();
  const [updateCarApiHandler, updateCarApiData] = useLazyUpdateCarQuery();
  const [updateCarOnRelistPendingApiHandler, updateCarOnRelistPendingApiData] = useUpdateCarOnRelistPendingMutation();
  const [getVehicleDetailsApiHandler, getVehicleDetailsApiData] = useLazyGetVehicleDetailsQuery();
  // VIN modal states
  const [vinModalOpen, setVinModalOpen] = React.useState(false);
  const [vinData, setVinData] = React.useState(null);
  const [vinLoading, setVinLoading] = React.useState(false);
  const [vinError, setVinError] = React.useState('');

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      Location: Yup.string().max(500).required(t('validationFieldRequired')),
      VIN: Yup.string()
        .max(500)
        .required(t('validationFieldRequired'))
        .matches(/^[A-HJ-NPR-Z0-9]{17}$/, 'VIN must be exactly 17 characters and contain no I, O, or Q'),
      Year: Yup.string().trim().max(4).required(t('validationFieldRequired')),
      Make: Yup.string().max(500).required(t('validationFieldRequired')),
      Model: Yup.string().max(500).required(t('validationFieldRequired')),
      Trim: Yup.string().max(500).required(t('validationFieldRequired')),
      Mileage: Yup.number().min(500).required(t('validationFieldRequired')),
      Mileage_Unit: Yup.string().oneOf([mileageUnits.km, mileageUnits.mi]).required(t('validationFieldRequired')),
      color: Yup.string().max(500).required(t('validationFieldRequired')),
      key: Yup.string().max(500).required(t('validationFieldRequired')),
      transmission: Yup.string().max(500).required(t('validationFieldRequired')),
      exteriorDamage: Yup.array().min(1, t('validationExteriorDamageRequired')),
      interiorDamage: Yup.array().min(1, t('validationInteriorDamageRequired')),
      tires: Yup.array().required(t('validationFieldRequired')),
      accessories: Yup.array().required(t('validationFieldRequired')),
      check_driving_condition: Yup.string()
        .required(t('validationFieldRequired'))
        .when(['Mileage', 'Mileage_Unit'], ([mileage, mileageUnit], schema) => {
          // convert mileage to kilometers if needed
          let mileageInKm = mileage;

          if (mileageUnit === mileageUnits.mi) {
            mileageInKm = mileage * 1.60934; // convert miles to km
          }

          console.log(mileageInKm, `mileageInKm`);

          if (mileageInKm > mileageDrivingCondition) {
            return schema.oneOf([DrivingCondition['Sold AS IS']], t('validationMileageDrivingCondition'));
          } else {
            return schema.oneOf([DrivingCondition['Run and Drive'], DrivingCondition['Sold AS IS']]);
          }
        }),
    }),
    onSubmit: async (values) => {
      // ... (rest of the onSubmit logic remains the same)
      const payload = {
        ...values,
      };
      const { interiorDamage, exteriorDamage, tires, accessories, transmission, color, key, ...rest } = values;

      // ... (rest of the payload processing remains the same)
      for (let i = 0; i < DamageOptions.interior.length; i++) {
        const idx = values?.interiorDamage?.findIndex?.((item) => item === DamageOptions.interior[i]);
        if (idx >= 0) {
          payload[backendMappedValuesStep1.interiorDamage[values?.interiorDamage?.[idx]]] = '1';
        } else {
          payload[backendMappedValuesStep1.interiorDamage[DamageOptions.interior[i]]] = '0';
        }
      }

      for (let i = 0; i < DamageOptions.exterior.length; i++) {
        const idx = values?.exteriorDamage?.findIndex?.((item) => item === DamageOptions.exterior[i]);
        if (idx >= 0) {
          payload[backendMappedValuesStep1.exteriorDamage[values?.exteriorDamage?.[idx]]] = '1';
        } else {
          payload[backendMappedValuesStep1.exteriorDamage[DamageOptions.exterior[i]]] = '0';
        }
      }

      for (let i = 0; i < DamageOptions.tires.length; i++) {
        const idx = values?.tires?.findIndex?.((item) => item === DamageOptions.tires[i]);
        if (idx >= 0) {
          payload[backendMappedValuesStep1.tires[values?.tires?.[idx]]] = '1';
        } else {
          payload[backendMappedValuesStep1.tires[DamageOptions.tires[i]]] = '0';
        }
      }

      for (let i = 0; i < DamageOptions.accessories.length; i++) {
        const idx = values?.accessories?.findIndex?.((item) => item === DamageOptions.accessories[i]);
        if (idx >= 0) {
          payload[backendMappedValuesStep1.accessories[values?.accessories?.[idx]]] = '1';
        } else {
          payload[backendMappedValuesStep1.accessories[DamageOptions.accessories[i]]] = '0';
        }
      }

      payload['transmission'] = values?.transmission;
      payload['Color'] = values?.color;
      payload['Car_Keys'] = values?.key;

      for (let i = 0; i < Object.entries(rest)?.length; i++) {
        payload[[Object.entries(rest)?.[i]?.[0]]] = Object.entries(rest)?.[i]?.[1];
      }

      const formData = new FormData();

      for (let i = 0; i < Object.entries(payload)?.length; i++) {
        formData.append(Object.entries(payload)?.[i]?.[0], Object.entries(payload)?.[i]?.[1]);
      }

      let response;
      if (!isEditing && !previouslySubmittedForms?.generalInfo) {
        response = await submitCarForm({
          formData,
          endpoint: `addnewvehicle`,
        });
      }

      formData.append(`Seller_Id`, auth?.user_id);

      if (response?.isSuccess || !response) {
        if (!lodash?.isEqual(previouslySubmittedForms?.generalInfo, formik?.values)) {
          let updateCarResponse;

          if (getVehicleDetailsApiData?.data?.vehicle?.car_label_status === CAR_STATUS.RELIST_PENDING) {
            updateCarResponse = await updateCarOnRelistPendingApiHandler({
              formData,
              Vehicle_Id: response?.data?.Vehicle_Id || vehicle_id,
            });
          } else {
            updateCarResponse = await updateCarApiHandler({
              formData,
              endpoint: `/updatevehicle/${response?.data?.Vehicle_Id || vehicle_id}/normaledit`,
            });
          }

          if (
            updateCarResponse?.isSuccess ||
            ('status' in (updateCarResponse?.data || {}) && updateCarResponse?.data?.status)
          ) {
            if (response?.data?.Vehicle_Id) {
              dispatch(
                setVehicleId({
                  vehicle_id: response?.data?.Vehicle_Id,
                })
              );
            }

            dispatch(
              setMyGarageMultipleFields({
                forms: {
                  ...forms,
                  generalInfo: formik?.values,
                },
                previouslySubmittedForms: {
                  ...previouslySubmittedForms,
                  generalInfo: formik?.values,
                },
              })
            );
            dispatch(setActiveStep(MyGarageSteps[1]));
          } else {
            dispatch(
              setAlertDialog({
                open: true,
                title: t('errorTitle'),
                message: updateCarResponse?.error?.message || t('someErrorOccurred'),
              })
            );
          }
        } else {
          dispatch(setActiveStep(MyGarageSteps[1]));
        }
      } else {
        dispatch(
          setAlertDialog({
            open: true,
            title: t('errorTitle'),
            message: response?.error?.message || t('someErrorOccurred'),
          })
        );
      }
    },
  });

  // Handle VIN input change and auto-decode
  const handleVINChange = async (event) => {
    const vinValue = event.target.value.toUpperCase();
    formik.setFieldValue('VIN', vinValue);
    // formik.handleChange(event);
    setVinError('');

    // Auto-decode when VIN is 17 characters
    if (vinValue.length === 17) {
      setVinLoading(true);
      try {
        const decodedData = await decodeVIN(vinValue);
        setVinData(decodedData);
        setVinModalOpen(true);
      } catch (error) {
        setVinError(error.message);
      } finally {
        setVinLoading(false);
      }
    }
  };

  // Handle adding VIN data to form
  const handleAddVINData = () => {
    if (vinData) {
      formik.setValues((prev) => ({
        ...prev,
        Make: vinData.Make || prev.Make,
        Model: vinData.Model || prev.Model,
        Year: vinData.Year || prev.Year,
        Trim: vinData.Trim || prev.Trim,
      }));
    }
    setVinModalOpen(false);
    setVinData(null);
  };

  const options = [
    {
      value: TransmissionOptions.AUTOMATIC,
      label: t('automatic'),
    },
    { value: TransmissionOptions.MANUAL, label: t('manual') },
  ];

  const translatedCarColorOptions = CarColorOptions.map((colorOption) => ({
    value: colorOption.value,
    label: t(`colorOption${colorOption.label.replace(/\s/g, '')}`), // Changed to flat key
  }));

  const translatedCarKeyOptions = CarKeyOptions.map((keyOption) => ({
    value: keyOption.value,
    label: t(`keyOption${keyOption.label.replace(/\s/g, '')}`), // Changed to flat key
  }));
  React.useEffect(() => {
    if (forms?.generalInfo) {
      formik.setValues(forms?.generalInfo);
    }
  }, []);

  React.useEffect(() => {
    if (onMount.current.setGeneralInfoForm) {
      console.log(formik.values, `setGeneralInfoForm`);
      dispatch(setGeneralInfoForm(formik?.values));
    } else {
      onMount.current.setGeneralInfoForm = true;
    }
  }, [formik?.values]);

  React.useEffect(() => {
    if (onMount.current.checkDrivingCondition) {
      let parsedMileage = parseInt(formik?.values?.Mileage);

      if (formik?.values?.Mileage_Unit === mileageUnits.mi) {
        parsedMileage = formik?.values?.Mileage * 1.60934; // convert miles to km
      }

      if (parsedMileage > mileageDrivingCondition) {
        formik.setValues((values) => {
          return {
            ...values,
            check_driving_condition: DrivingCondition?.['Sold AS IS'],
          };
        });
      } else {
        formik.setValues((values) => {
          return {
            ...values,
            check_driving_condition: DrivingCondition?.['Run and Drive'],
          };
        });
      }
    } else {
      onMount.current.checkDrivingCondition = true;
    }
  }, [formik?.values?.Mileage]);

  React.useEffect(() => {
    if (onMount.current.setTouchedInteriorDamage) {
      if (onMount?.current?.interiorDamage) {
        formik.setTouched({
          ...formik.touched,
          interiorDamage: true,
        });
      } else {
        onMount.current.interiorDamage = true;
      }
    } else {
      onMount.current.setTouchedInteriorDamage = true;
    }
  }, [formik?.values?.interiorDamage]);

  React.useEffect(() => {
    if (onMount.current.setTouchedExteriorDamage) {
      if (onMount?.current?.exteriorDamage) {
        formik.setTouched({
          ...formik.touched,
          exteriorDamage: true,
        });
      } else {
        onMount.current.exteriorDamage = true;
      }
    } else {
      onMount.current.setTouchedExteriorDamage = true;
    }
  }, [formik?.values?.exteriorDamage]);

  React.useEffect(() => {
    if (!vehicle_id) {
      return;
    }

    (async () => {
      await getVehicleDetailsApiHandler({
        vehicleId: vehicle_id,
      });
    })();
  }, [vehicle_id]);

  console.log(formik, `GeneralInfo_form`);

  return (
    <>
      <Typography color="textPrimary" m={'40px 0 40px 0'} textAlign={'center'}>
        {t('shareDetailsSubtext')}
      </Typography>

      <Container sx={formContainer}>
        <form onSubmit={formik.handleSubmit} style={formStyle}>
          <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={labelText}>
                {t('location')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth size="small" sx={formControl}>
                <CustomSelect
                  name="Location"
                  value={formik.values.Location}
                  onChange={formik.handleChange}
                  fullWidth
                  sx={select}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: theme.palette.text.disabled }}>{t('selectLocation')}</span>;
                    }
                    return options.find((opt) => opt.value === selected)?.label || selected;
                  }}
                  options={[
                    {
                      label: t('selectLocation'),
                      value: '',
                    },
                    ...(ontarioCitiesArray)
                  ]}
                />
              </FormControl>
              {formik.touched.Location && formik.errors.Location && (
                <FieldErrorText>{formik.errors.Location}</FieldErrorText>
              )}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={{ ...labelText }}>
                {t('vinNumber')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  name="VIN"
                  value={formik.values.VIN}
                  onChange={handleVINChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  size="small"
                  sx={{ ...formControl }}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                  placeholder="Enter 17-character VIN"
                  error={Boolean((formik.touched.VIN && formik.errors.VIN) || vinError)}
                  InputProps={{
                    endAdornment: vinLoading && <CircularProgress size={20} />,
                  }}
                />
                {formik.touched.VIN && formik.errors.VIN && <FieldErrorText>{formik.errors.VIN}</FieldErrorText>}
                {vinError && <FieldErrorText>{vinError}</FieldErrorText>}
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={labelText}>
                {t('year')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                name="Year"
                value={formik.values.Year}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                sx={formControl}
                disabled={
                  !isVehicleDataEditEnabled({
                    car: selectedVehicle,
                  })
                }
              />
              {formik.touched.Year && formik.errors.Year && <FieldErrorText>{formik.errors.Year}</FieldErrorText>}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={labelText}>
                {t('make')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                name="Make"
                value={formik.values.Make}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                sx={formControl}
                disabled={
                  !isVehicleDataEditEnabled({
                    car: selectedVehicle,
                  })
                }
              />
              {formik.touched.Make && formik.errors.Make && <FieldErrorText>{formik.errors.Make}</FieldErrorText>}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={labelText}>
                {t('model')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                name="Model"
                value={formik.values.Model}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                sx={formControl}
                disabled={
                  !isVehicleDataEditEnabled({
                    car: selectedVehicle,
                  })
                }
              />
              {formik.touched.Model && formik.errors.Model && <FieldErrorText>{formik.errors.Model}</FieldErrorText>}
            </Grid>
            {/* </Grid> */}

            {/* <Grid container spacing={2} sx={{ mb: 2 }}> */}
            <Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
              <Typography sx={labelText}>
                {t('trim')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <TextField
                name="Trim"
                value={formik.values.Trim}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                sx={formControl}
                disabled={
                  !isVehicleDataEditEnabled({
                    car: selectedVehicle,
                  })
                }
              />
              {formik.touched.Trim && formik.errors.Trim && <FieldErrorText>{formik.errors.Trim}</FieldErrorText>}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
              <Typography sx={labelText}>
                {t('odometer')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CommaSeparatedInput
                  name="Mileage"
                  value={formik.values.Mileage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  size="small"
                  sx={{
                    ...formControl,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    },
                    width: `calc(100% - 5rem)`,
                  }}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                />

                <CustomSelect
                  name="Mileage_Unit"
                  value={formik.values.Mileage_Unit}
                  onChange={formik.handleChange}
                  fullWidth
                  sx={{
                    ...select,
                    '& fieldset': {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    },
                    width: '5rem !important',
                    minWidth: '0',
                  }}
                  options={[
                    {
                      value: mileageUnits.km,
                      label: mileageUnits.km,
                    },
                    {
                      value: mileageUnits.mi,
                      label: mileageUnits.mi,
                    },
                  ]}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                />
              </Box>
              {/* <TextField
                name="Mileage"
                value={formik.values.Mileage}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                sx={formControl}
              /> */}
              {formik.touched.Mileage && formik.errors.Mileage && (
                <FieldErrorText>{formik.errors.Mileage}</FieldErrorText>
              )}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
              <Typography sx={labelText}>
                {t('color')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth size="small" sx={formControl}>
                <CustomSelect
                  name="color"
                  value={formik.values.color}
                  onChange={formik.handleChange}
                  fullWidth
                  sx={select}
                  options={translatedCarColorOptions}
                  disabled={
                    !isVehicleDataEditEnabled({
                      car: selectedVehicle,
                    })
                  }
                />
              </FormControl>
              {formik.touched.color && formik.errors.color && <FieldErrorText>{formik.errors.color}</FieldErrorText>}
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
              <Typography sx={labelText}>
                {t('keys')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box
                sx={{
                  width: '100%',
                }}
              >
                <FormControl fullWidth size="small" sx={formControl}>
                  <CustomSelect
                    name="key"
                    value={formik.values.key}
                    onChange={formik.handleChange}
                    sx={select}
                    options={translatedCarKeyOptions}
                    disabled={
                      !isVehicleDataEditEnabled({
                        car: selectedVehicle,
                      })
                    }
                  />
                </FormControl>
                {formik.touched.key && formik.errors.key && <FieldErrorText>{formik.errors.key}</FieldErrorText>}
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, lg: 3 }}>
              <Typography sx={labelText}>
                {t('transmissionQuestion')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <ToggleButtonGroup
                value={formik.values.transmission}
                exclusive
                onChange={(_, value) => value && formik.setFieldValue('transmission', value)}
                sx={toggleButtonGroup}
                disabled={
                  !isVehicleDataEditEnabled({
                    car: selectedVehicle,
                  })
                }
              >
                {options.map((option, index) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    sx={getToggleButtonStyles(formik.values.transmission === option.value, index, options.length)}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {formik.touched.transmission && formik.errors.transmission && (
                <FieldErrorText>{formik.errors.transmission}</FieldErrorText>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item size={12} sx={tiresAccessoriesContainer}>
              <Grid container spacing={1}>
                {DamageOptions.tires.map((option) => (
                  <Grid item key={option} size={{ xs: 12, sm: 'auto', md: 'auto', lg: 'auto' }}>
                    <CustomCheckbox
                      fullWidth
                      key={option}
                      label={t(`damageOptions${option.replace(/\s|\.|\(|\)/g, '')}`)} // Changed to flat key
                      selected={formik.values.tires.includes(option)}
                      onClick={() =>
                        formik.setFieldValue(
                          'tires',
                          formik.values.tires.includes(option)
                            ? formik.values.tires.filter((d) => d !== option)
                            : [...formik.values.tires, option]
                        )
                      }
                      disabled={
                        !isVehicleDataEditEnabled({
                          car: selectedVehicle,
                        })
                      }
                    />
                  </Grid>
                ))}
                {DamageOptions.accessories.map((option) => (
                  <Grid item key={option} size={{ xs: 12, sm: 'auto', md: 'auto', lg: 'auto' }}>
                    <CustomCheckbox
                      key={option}
                      label={t(`damageOptions${option.replace(/\s|\.|\(|\)/g, '')}`)} // Changed to flat key
                      selected={formik.values.accessories.includes(option)}
                      onClick={() =>
                        formik.setFieldValue(
                          'accessories',
                          formik.values.accessories.includes(option)
                            ? formik.values.accessories.filter((d) => d !== option)
                            : [...formik.values.accessories, option]
                        )
                      }
                      disabled={
                        !isVehicleDataEditEnabled({
                          car: selectedVehicle,
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item sx={damageContainer}>
              <Typography color="text.form" mb={1}>
                {t('anyExteriorBodyDamage')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Grid container spacing={1}>
                {DamageOptions.exterior.map((damage) => (
                  <Grid item key={damage} size={{ xs: 12, sm: 'auto', md: 'auto', lg: 'auto' }}>
                    <CustomCheckbox
                      key={damage}
                      label={t(`damageOptions${damage.replace(/\s|\.|\(|\)/g, '')}`)} // Changed to flat key
                      selected={formik.values.exteriorDamage.includes(damage)}
                      onClick={() => {
                        if (formik.values.exteriorDamage.includes(damage)) {
                          formik.setFieldValue(
                            `exteriorDamage`,
                            formik.values.exteriorDamage.filter((d) => d !== damage)
                          );
                        } else if (damage === DamageOptionsConstants.exterior['Mint condition']) {
                          formik.setFieldValue(`exteriorDamage`, [damage]);
                        } else {
                          if (
                            !formik.values.exteriorDamage.includes(DamageOptionsConstants.exterior['Mint condition'])
                          ) {
                            formik.setFieldValue(`exteriorDamage`, [...formik.values.exteriorDamage, damage]);
                          }
                        }
                      }}
                      disabled={
                        !isVehicleDataEditEnabled({
                          car: selectedVehicle,
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              {formik?.touched?.exteriorDamage && formik?.errors?.exteriorDamage && (
                <Box
                  sx={{
                    ...errorContainer,
                    justifyContent: 'flex-start',
                  }}
                >
                  <FieldErrorText>{formik.errors.exteriorDamage}</FieldErrorText>
                </Box>
              )}
            </Grid>

            <Grid item sx={{ width: '100%', marginTop: '0.5rem' }}>
              <Typography color="text.form" mb={1}>
                {t('anyInteriorDamage')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Grid container spacing={1}>
                {DamageOptions.interior.map((damage) => (
                  <Grid item key={damage} size={{ xs: 12, sm: 'auto', md: 'auto', lg: 'auto' }}>
                    <CustomCheckbox
                      key={damage}
                      label={t(`damageOptions${damage.replace(/\s|\.|\(|\)/g, '')}`)} // Changed to flat key
                      selected={formik.values.interiorDamage.includes(damage)}
                      onClick={() => {
                        if (formik.values.interiorDamage.includes(damage)) {
                          formik.setFieldValue(
                            `interiorDamage`,
                            formik.values.interiorDamage.filter((d) => d !== damage)
                          );
                        } else if (damage === DamageOptionsConstants.exterior['Mint condition']) {
                          formik.setFieldValue(`interiorDamage`, [damage]);
                        } else {
                          if (
                            !formik.values.interiorDamage.includes(DamageOptionsConstants.exterior['Mint condition'])
                          ) {
                            formik.setFieldValue(`interiorDamage`, [...formik.values.interiorDamage, damage]);
                          }
                        }
                      }}
                      // onClick={() =>
                      //   formik.setFieldValue(
                      //     "interiorDamage",
                      //     formik.values.interiorDamage.includes(damage)
                      //       ? formik.values.interiorDamage.filter(
                      //         (d) => d !== damage
                      //       )
                      //       : [...formik.values.interiorDamage, damage]
                      //   )
                      // }
                      disabled={
                        !isVehicleDataEditEnabled({
                          car: selectedVehicle,
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              {formik?.touched?.interiorDamage && formik?.errors?.interiorDamage && (
                <Box
                  sx={{
                    ...errorContainer,
                    justifyContent: 'flex-start',
                  }}
                >
                  <FieldErrorText>{formik.errors.interiorDamage}</FieldErrorText>
                </Box>
              )}
            </Grid>

            <Grid
              item
              sx={{
                width: '100%',
                marginTop: '0.5rem',
              }}
            >
              <Typography color="text.form" mb={1}>
                {t('checkDrivingCondition')} <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(DrivingCondition).map(([key, value]) => (
                  <Grid item key={value} size={{ xs: 12, sm: 'auto', md: 'auto', lg: 'auto' }}>
                    <CustomCheckbox
                      key={value}
                      label={t(`drivingCondition${key.replace(/\s/g, '')}`)} // Changed to flat key
                      selected={formik.values.check_driving_condition === value}
                      onClick={() => {
                        if (
                          value === DrivingCondition['Run and Drive'] &&
                          formik.values?.Mileage > mileageDrivingCondition
                        ) {
                          dispatch(
                            setAlertDialog({
                              open: true,
                              title: 'Alert!',
                              message: `Driving condition must be "Sold AS IS" when mileage is greater than ${getCommaFormattedPrice(
                                mileageDrivingCondition
                              )}`,
                            })
                          );
                          return;
                        }

                        formik.setFieldValue('check_driving_condition', value);
                      }}
                      disabled={
                        !isVehicleDataEditEnabled({
                          car: selectedVehicle,
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>

              {formik.values?.check_driving_condition === DrivingCondition['Sold AS IS'] && (
                <FieldErrorText>
                  {t("Cars with mileage above 160,000 km or 100,000 miles are sold as is")}
                </FieldErrorText>
              )}

              {formik.touched.check_driving_condition && formik.errors.check_driving_condition && (
                <FieldErrorText>{formik.errors.check_driving_condition}</FieldErrorText>
              )}
            </Grid>
          </Grid>

          <NavigationButtons
            onNextClick={formik.handleSubmit}
            previousFormHandler={previousFormHandler}
            nextButtonText={t('next')}
            prevButtonText={t('previous')}
            isLoading={
              submitCarFormData.isLoading || updateCarApiData.isLoading || updateCarOnRelistPendingApiData?.isLoading
            }
          />
        </form>
      </Container>
      <Dialog open={vinModalOpen} onClose={() => setVinModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <IconButton onClick={() => setVinModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {vinData && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                Vehicle Information:
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '60px' }}>
                    Make:
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {vinData.Make || 'Not available'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '60px' }}>
                    Model:
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {vinData.Model || 'Not available'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '60px' }}>
                    Year:
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {vinData.Year || 'Not available'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '60px' }}>
                    Trim:
                  </Typography>
                  <Typography variant="body1" color="primary">
                    {vinData.Trim || 'Not available'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Would you like to auto-fill this information into the form?
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setVinModalOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddVINData} variant="contained" color="primary">
            Add to Form
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
